import React, { useCallback, useContext, useMemo } from 'react';
import pt from 'prop-types';
import get from 'lodash/get';
import { EasyblocksBackendContext } from 'components/storefront/editor/backendContext';
import {
  getFormValues,
  changeFormValue,
} from 'components/storefront/editor/utils/easyblocks';

function getLocaleConfigPath(easyblocksFieldName, localeFieldName = '$locale') {
  const localeValueFieldName = localeFieldName.replace(
    '$locale',
    '$locale.value',
  );
  const lastDotIndex = easyblocksFieldName.lastIndexOf('.');

  if (lastDotIndex === -1) {
    return localeValueFieldName;
  }

  const pathPrefix = easyblocksFieldName.slice(0, lastDotIndex);

  return `${pathPrefix}.${localeValueFieldName}`;
}

function shouldPostLocaleValue(localeFieldName) {
  if (typeof localeFieldName !== 'string') {
    return false;
  }

  return localeFieldName.startsWith('$locale');
}

function withLocale(WidgetComponent) {
  const WrappedWidget = (props) => {
    const { field, onChange } = props;
    const { name, params } = field;
    const { localized = true } = params || {};

    const { loaded } = useContext(EasyblocksBackendContext);

    const formValues = getFormValues();

    const localeValue = useMemo(() => {
      const localeConfigPath = getLocaleConfigPath(name);

      return get(formValues, localeConfigPath);
    }, [formValues, name]);

    const onChangeHandler = useCallback(
      (event, value, field) => {
        const { name: localeFieldName } = field?.props || {};

        if (!shouldPostLocaleValue(localeFieldName)) {
          return onChange(event, value);
        }

        const path = getLocaleConfigPath(name, localeFieldName);

        changeFormValue(path, value);
      },
      [name, onChange],
    );

    if (!loaded) {
      return null;
    }

    return (
      <WidgetComponent
        {...props}
        localeValue={localeValue}
        localized={localized}
        onChange={onChangeHandler}
      />
    );
  };

  WrappedWidget.propTypes = {
    field: pt.object,
    onChange: pt.func,
  };

  return React.memo(WrappedWidget);
}

export default withLocale;
